import type {
  AttendanceRecord,
  CalendarEvent,
  Classroom,
  DailyRecord,
  HomeroomHomeworkRecord,
  HomeworkAssignment,
  HomeworkRecord,
  QuizDocument,
  SeatingChart,
  Student,
  StudentRoutineItem,
  Workspace,
} from "@classroom/domain";
import { academicYearTerms, hongKongDate, isHongKongPublicHolidayDate, schoolYearStart } from "@classroom/domain";

const now = new Date().toISOString();
const yesterday = new Date(Date.now() - 86_400_000);
const demoRecordDate = new Date(yesterday.getTime() - yesterday.getTimezoneOffset() * 60_000)
  .toISOString()
  .slice(0, 10);
const demoDate = (daysFromToday: number) => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};

export function demoSchoolDate(
  value: string,
  holidays: readonly Pick<CalendarEvent, "date" | "endDate" | "schoolKind">[] = [],
  days = 1,
) {
  // Short teaching blocks must fit between Sundays; holiday ranges themselves stay intact.
  if (!Number.isInteger(days) || days < 1 || days > 6) throw new Error("Invalid Demo teaching block length");
  const date = new Date(`${value}T12:00:00Z`);
  while (
    Array.from({ length: days }, (_, index) =>
      new Date(date.getTime() + index * 86_400_000).toISOString().slice(0, 10),
    ).some(
      (day) =>
        isHongKongPublicHolidayDate(day) ||
        holidays.some(
          (holiday) =>
            (holiday.schoolKind === "schoolHoliday" || holiday.schoolKind === "publicHoliday") &&
            day >= holiday.date &&
            day <= (holiday.endDate || holiday.date),
        ),
    )
  )
    date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

const demoSchoolYear = schoolYearStart(hongKongDate());

export const demoWorkspace: Workspace = {
  id: "preview-workspace",
  name: "陳老師的課堂",
  ownerUid: "preview-user",
  accessMode: "trial",
  trialEndsAt: new Date(Date.now() + 6 * 86_400_000).toISOString(),
  retentionDays: 30,
  academicTerms: academicYearTerms(demoSchoolYear, 2),
  createdAt: now,
  updatedAt: now,
};

const demoSchoolCalendar = createDemoSchoolCalendar(demoSchoolYear);

export const demoClasses: Classroom[] = [
  {
    id: "c1",
    workspaceId: demoWorkspace.id,
    name: "2A 公民科",
    classRole: "teaching",
    active: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "c3",
    workspaceId: demoWorkspace.id,
    name: "2B 中文科",
    classRole: "teaching",
    active: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "c4",
    workspaceId: demoWorkspace.id,
    name: "3A 英文科",
    classRole: "teaching",
    active: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "c5",
    workspaceId: demoWorkspace.id,
    name: "3B 數學科",
    classRole: "teaching",
    active: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "c2",
    workspaceId: demoWorkspace.id,
    name: "3C 班主任",
    classRole: "homeroom",
    active: true,
    createdAt: now,
    updatedAt: now,
  },
];

const teachingStudentNames = [
  "陳幼羚",
  "李允行",
  "黃允行",
  "張英秀",
  "林修端",
  "何幼羚",
  "周向華",
  "鄭一心",
  "郭有容",
  "吳幼羚",
  "梁允行",
  "羅修端",
];
const teachingStudents: Student[] = Array.from({ length: 12 }, (_, index) => ({
  id: `s${index + 1}`,
  workspaceId: demoWorkspace.id,
  classId: "c1",
  number: index + 1,
  name: teachingStudentNames[index]!,
  gender: index % 2 === 0 ? "M" : "F",
  active: true,
  createdAt: now,
  updatedAt: now,
}));
const homeroomStudentNames = [
  "陳念慈",
  "蔡芷澄",
  "方英秀",
  "林思賢",
  "曹一心",
  "關修端",
  "鄭以晴",
  "馮英秀",
  "張映彤",
  "葉念慈",
  "鄧思賢",
  "曾芷澄",
  "關家寶",
  "張思賢",
  "謝有容",
  "郭允謙",
  "鄭念羚",
  "何向華",
  "馮修端",
  "許念慈",
  "關英秀",
  "林有容",
  "周念羚",
  "施允行",
  "陳允行",
  "何有容",
  "鄭幼羚",
  "羅樂言",
  "周修端",
  "黃以晴",
  "黃向華",
  "方念羚",
  "鄭映彤",
  "蘇映彤",
  "周允謙",
];
const additionalTeachingStudentNames: Record<string, string[]> = {
  c3: [
    "許念羚",
    "林幼羚",
    "何念慈",
    "曹思賢",
    "馮念羚",
    "袁一心",
    "施思賢",
    "唐念羚",
    "蔡家寶",
    "黃英秀",
    "施樂言",
    "蔡允謙",
    "關有容",
    "李家寶",
    "吳修端",
    "鄧樂言",
    "林允行",
    "梁念慈",
  ],
  c4: [
    "馮一心",
    "蔡映彤",
    "鍾允謙",
    "周映彤",
    "李有容",
    "鍾念羚",
    "陳一心",
    "曾樂言",
    "施有容",
    "朱家寶",
    "馮向華",
    "麥有容",
    "高芷澄",
    "關樂言",
    "高念慈",
    "關以晴",
    "黃允謙",
    "潘以晴",
    "曹幼羚",
    "顧念羚",
    "胡芷澄",
    "麥以晴",
    "周念慈",
    "鄧向華",
    "潘念羚",
    "黃芷澄",
    "施以晴",
  ],
  c5: [
    "蔡有容",
    "方以晴",
    "曾一心",
    "蘇向華",
    "朱幼羚",
    "鄭念慈",
    "許幼羚",
    "鄧一心",
    "顧允謙",
    "周允行",
    "曹家寶",
    "潘英秀",
    "關念羚",
    "施修端",
    "袁思賢",
    "盧以晴",
    "葉允謙",
    "郭樂言",
    "杜映彤",
    "蔡幼羚",
    "謝允行",
    "胡映彤",
    "盧允謙",
    "梁以晴",
    "袁樂言",
    "陳樂言",
    "袁念羚",
    "郭英秀",
    "李念慈",
    "顧有容",
    "蘇有容",
    "葉芷澄",
    "羅幼羚",
    "馬向華",
    "林念羚",
    "吳一心",
  ],
};
const additionalTeachingStudents: Student[] = [
  { classId: "c3", count: 18 },
  { classId: "c4", count: 27 },
  { classId: "c5", count: 36 },
].flatMap(({ classId, count }) =>
  Array.from({ length: count }, (_, index) => ({
    id: `${classId}s${index + 1}`,
    workspaceId: demoWorkspace.id,
    classId,
    number: index + 1,
    name: additionalTeachingStudentNames[classId]![index]!,
    gender: index % 2 === 0 ? "M" : "F",
    active: true,
    createdAt: now,
    updatedAt: now,
  })),
);
const homeroomStudents: Student[] = Array.from({ length: 35 }, (_, index) => ({
  id: `h${index + 1}`,
  workspaceId: demoWorkspace.id,
  classId: "c2",
  number: index + 1,
  name: homeroomStudentNames[index]!,
  gender: index % 2 === 0 ? "M" : "F",
  active: true,
  createdAt: now,
  updatedAt: now,
}));

export const demoStudents: Student[] = [...teachingStudents, ...additionalTeachingStudents, ...homeroomStudents];

export const demoDailyRecords: DailyRecord[] = demoClasses
  .filter((classroom) => classroom.classRole === "teaching")
  .flatMap((classroom, classIndex) =>
    [-9, -7, -5, -3, -1, 0].map((days, dayIndex) => ({
      id: `${classroom.id}_${demoDate(days)}`,
      workspaceId: demoWorkspace.id,
      classId: classroom.id,
      date: demoDate(days),
      students: Object.fromEntries(
        demoStudents
          .filter((student) => student.classId === classroom.id)
          .map((student, index) => [
            student.id,
            {
              answers: index === 11 ? 0 : (index + dayIndex + classIndex) % 4,
              missingMaterials: index === 0 ? dayIndex % 2 === 0 : (index + dayIndex + classIndex) % 7 === 0,
              ...(index === 0 && dayIndex === 0 ? { note: "虛構示例：提醒下次帶工作紙" } : {}),
            },
          ]),
      ),
      updatedAt: now,
    })),
  );

export const demoHomeworkTopics: Record<string, string[]> = {
  c1: ["公民責任", "社區生活", "媒體資訊", "公共資源", "城市發展", "環境保護", "公民參與", "單元整合"],
  c3: ["記敘文閱讀", "人物描寫", "修辭手法", "文言文理解", "說明文閱讀", "句子改寫", "段落寫作", "單元整合"],
  c4: [
    "Reading Comprehension",
    "Past Tense",
    "Adjectives",
    "Prepositions",
    "Letter Writing",
    "Reported Speech",
    "Short Stories",
    "Unit Review",
  ],
  c5: ["整數運算", "代數式", "一元一次方程", "百分比", "幾何圖形", "面積與體積", "統計圖表", "單元整合"],
};

export const demoHomeworkAssignments: HomeworkAssignment[] = demoClasses
  .filter((classroom) => classroom.classRole === "teaching")
  .flatMap((classroom) =>
    [
      { category: "homework" as const, title: `${demoHomeworkTopics[classroom.id]![0]}工作紙`, maxScore: 10, days: -1 },
      { category: "assessment" as const, title: "單元一測驗", maxScore: 100, days: -3 },
      { category: "dictation" as const, title: "核心詞語默書", maxScore: 20, days: -5 },
    ].map((example) => ({
      id: `demo-${classroom.id}-${example.category}`,
      workspaceId: demoWorkspace.id,
      classId: classroom.id,
      category: example.category,
      title: example.title,
      maxScore: example.maxScore,
      dueDate: demoSchoolDate(demoDate(example.days), demoSchoolCalendar),
      note: "虛構示例，可試改繳交狀態及分數；重新載入後還原。",
      createdAt: now,
      updatedAt: now,
    })),
  );

export const demoHomeworkRecords: HomeworkRecord[] = demoHomeworkAssignments.map((assignment) => ({
  id: `assignment-${assignment.id}`,
  workspaceId: demoWorkspace.id,
  classId: assignment.classId,
  assignmentId: assignment.id,
  students: Object.fromEntries(
    demoStudents
      .filter((student) => student.classId === assignment.classId)
      .map((student, index) => [
        student.id,
        index === 0
          ? {
              status: "missing",
              note: assignment.category === "homework" ? "提醒下次上課補交" : "下次上課安排補考",
            }
          : index === 1
            ? {
                status: "late",
                score: Math.round(assignment.maxScore! * 0.7),
                note: assignment.category === "homework" ? "已補交，完成訂正" : "已補考，分數已覆核",
              }
            : index === 2 && assignment.category === "homework"
              ? { status: "complete", score: 0, note: "已批改，需訂正" }
              : { status: "complete", score: Math.round(assignment.maxScore! * (0.5 + (index % 6) * 0.1)) },
      ]),
  ),
  updatedAt: now,
}));

export const demoAttendanceRecords: AttendanceRecord[] = [
  {
    id: `c2_${demoRecordDate}`,
    workspaceId: demoWorkspace.id,
    classId: "c2",
    date: demoRecordDate,
    studentIds: homeroomStudents.map((student) => student.id),
    exceptions: { h1: "late", h2: "absent" },
    updatedAt: now,
  },
];

export const demoHomeroomHomeworkRecords: HomeroomHomeworkRecord[] = [
  {
    id: `c2_${demoRecordDate}_math_worksheet`,
    workspaceId: demoWorkspace.id,
    classId: "c2",
    date: demoRecordDate,
    subject: "數學",
    taskType: "worksheet",
    taskDetail: "工作紙三",
    missingStudentIds: ["h1", "h3"],
    createdAt: now,
    updatedAt: now,
  },
  {
    id: `c2_${demoRecordDate}_chinese_notes`,
    workspaceId: demoWorkspace.id,
    classId: "c2",
    date: demoRecordDate,
    subject: "中國語文",
    taskType: "notes",
    taskDetail: "第三課詞語筆記",
    missingStudentIds: ["h2"],
    createdAt: now,
    updatedAt: now,
  },
];

export const demoStudentRoutineItems: StudentRoutineItem[] = [
  {
    id: "routine-phone",
    workspaceId: demoWorkspace.id,
    classId: "c2",
    title: "未帶電話",
    studentIds: ["h4", "h11"],
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "routine-byod",
    workspaceId: demoWorkspace.id,
    classId: "c2",
    title: "未備 BYOD 裝置",
    studentIds: ["h8"],
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "routine-meal",
    workspaceId: demoWorkspace.id,
    classId: "c2",
    title: "不訂餐",
    studentIds: ["h2", "h16", "h23"],
    createdAt: now,
    updatedAt: now,
  },
];

export const demoSeatingCharts: SeatingChart[] = [
  ...demoClasses
    .filter((classroom) => classroom.classRole === "teaching")
    .map((classroom): SeatingChart => {
      const students = demoStudents.filter((student) => student.classId === classroom.id);
      const columns = students.length <= 12 ? 4 : 6;
      return {
        id: classroom.id,
        workspaceId: demoWorkspace.id,
        classId: classroom.id,
        rows: Math.ceil(students.length / columns),
        columns,
        view: "teacher",
        seats: Object.fromEntries(
          students.map((student, index) => [`${Math.floor(index / columns)}_${index % columns}`, student.id]),
        ),
        rowAisles: [],
        columnAisles: [],
        updatedAt: now,
      };
    }),
  {
    id: "c2",
    workspaceId: demoWorkspace.id,
    classId: "c2",
    rows: 5,
    columns: 7,
    view: "teacher",
    seats: Object.fromEntries(
      Array.from({ length: 35 }, (_, index) => [`${Math.floor(index / 7)}_${index % 7}`, `h${index + 1}`]),
    ),
    rowAisles: [],
    columnAisles: [],
    updatedAt: now,
  },
];

// Independently authored examples: never seed Demo with a school's calendar or identifiers.
export function createDemoSchoolCalendar(startYear: number): CalendarEvent[] {
  const date = (monthDay: string) => `${monthDay.slice(0, 2) >= "09" ? startYear : startYear + 1}-${monthDay}`;
  const week = (monthDay: string, days: number) => {
    const start = new Date(`${date(monthDay)}T12:00:00Z`);
    start.setUTCDate(start.getUTCDate() + ((8 - start.getUTCDay()) % 7));
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + days - 1);
    return { date: start.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10) };
  };
  const events: Pick<CalendarEvent, "date" | "endDate" | "title" | "type" | "schoolKind">[] = [
    { date: date("09-03"), title: "學年學習規劃", type: "school", schoolKind: "specialClass" },
    { date: date("09-18"), title: "校園閱讀日", type: "school", schoolKind: "specialDay" },
    { date: date("10-15"), title: "跨科探究日", type: "school", schoolKind: "specialClass" },
    { date: date("10-23"), title: "評估範圍公布", type: "reminder" },
    { date: date("10-28"), title: "教師研習日（學生放假）", type: "school", schoolKind: "schoolHoliday" },
    { ...week("11-09", 5), title: "第一階段評估週", type: "school", schoolKind: "exam" },
    { date: date("11-26"), title: "社區學習日", type: "school", schoolKind: "activity" },
    { date: date("12-04"), title: "學生作品展示日", type: "school", schoolKind: "specialDay" },
    {
      date: date("12-21"),
      endDate: date("01-02"),
      title: "冬季學校假期",
      type: "school",
      schoolKind: "schoolHoliday",
    },
    { date: date("01-04"), title: "新學期課堂安排", type: "school", schoolKind: "specialClass" },
    { date: date("01-22"), title: "學習回顧表截止", type: "reminder" },
    {
      date: date("02-04"),
      endDate: date("02-16"),
      title: "農曆新年學校假期",
      type: "school",
      schoolKind: "schoolHoliday",
    },
    { date: date("02-26"), title: "成長交流日", type: "school", schoolKind: "specialDay" },
    { ...week("03-08", 2), title: "專題研習日", type: "school", schoolKind: "activity" },
    { ...week("03-15", 5), title: "第二階段評估週", type: "school", schoolKind: "exam" },
    {
      date: date("04-02"),
      endDate: date("04-10"),
      title: "春季學校假期",
      type: "school",
      schoolKind: "schoolHoliday",
    },
    { date: date("05-07"), title: "校園服務日", type: "school", schoolKind: "activity" },
    { ...week("06-14", 5), title: "學年總結評估", type: "school", schoolKind: "exam" },
    { date: date("06-29"), title: "成果分享日", type: "school", schoolKind: "activity" },
    {
      date: date("07-12"),
      endDate: date("08-31"),
      title: "暑期學校假期",
      type: "school",
      schoolKind: "schoolHoliday",
    },
  ];
  return events.map((event, index) => {
    const start = Date.parse(`${event.date}T12:00:00Z`);
    const days = event.endDate ? (Date.parse(`${event.endDate}T12:00:00Z`) - start) / 86_400_000 + 1 : 1;
    const date = event.schoolKind === "schoolHoliday" ? event.date : demoSchoolDate(event.date, events, days);
    return {
      ...event,
      date,
      endDate: event.endDate
        ? new Date(Date.parse(`${date}T12:00:00Z`) + (days - 1) * 86_400_000).toISOString().slice(0, 10)
        : undefined,
      id: `demo-school-calendar-${startYear}-${index + 1}`,
      workspaceId: demoWorkspace.id,
      allDay: true,
      timezone: "Asia/Hong_Kong",
      note: "虛構校曆示例，日期與安排僅供操作示範。",
      createdAt: now,
      updatedAt: now,
    };
  });
}

export const demoEvents: CalendarEvent[] = [
  ...demoSchoolCalendar,
  {
    id: "e-public-holiday",
    workspaceId: demoWorkspace.id,
    date: "2026-10-01",
    title: "國慶日",
    type: "school",
    schoolKind: "publicHoliday",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "e-school",
    workspaceId: demoWorkspace.id,
    date: demoSchoolDate(demoDate(1), demoSchoolCalendar),
    title: "學校運動會",
    type: "school",
    schoolKind: "activity",
    note: "全校事項，所有班別均會顯示。",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "e1",
    workspaceId: demoWorkspace.id,
    classId: "c1",
    date: demoSchoolDate(demoDate(0), demoSchoolCalendar),
    title: "帶備課本及工作紙",
    type: "reminder",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "e2",
    workspaceId: demoWorkspace.id,
    classId: "c1",
    date: demoSchoolDate(demoDate(2), demoSchoolCalendar),
    title: "單元一小測",
    type: "assessment",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "e4",
    workspaceId: demoWorkspace.id,
    classId: "c1",
    date: demoSchoolDate(demoDate(8), demoSchoolCalendar),
    title: "第一課詞語默書",
    type: "dictation",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "e5",
    workspaceId: demoWorkspace.id,
    classId: "c3",
    date: demoSchoolDate(demoDate(2), demoSchoolCalendar),
    title: "第二課詞語默書",
    type: "dictation",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "e6",
    workspaceId: demoWorkspace.id,
    classId: "c3",
    date: demoSchoolDate(demoDate(5), demoSchoolCalendar),
    title: "資料回應題測驗",
    type: "assessment",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "e7",
    workspaceId: demoWorkspace.id,
    classId: "c4",
    date: demoSchoolDate(demoDate(4), demoSchoolCalendar),
    title: "公民責任小測",
    type: "assessment",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "e8",
    workspaceId: demoWorkspace.id,
    classId: "c4",
    date: demoSchoolDate(demoDate(8), demoSchoolCalendar),
    title: "第三課詞語默書",
    type: "dictation",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "e9",
    workspaceId: demoWorkspace.id,
    classId: "c5",
    date: demoSchoolDate(demoDate(5), demoSchoolCalendar),
    title: "核心詞語默書",
    type: "dictation",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "e10",
    workspaceId: demoWorkspace.id,
    classId: "c5",
    date: demoSchoolDate(demoDate(10), demoSchoolCalendar),
    title: "單元總結測驗",
    type: "assessment",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "e3",
    workspaceId: demoWorkspace.id,
    classId: "c2",
    date: demoSchoolDate(demoDate(3), demoSchoolCalendar),
    title: "班會及值日生安排",
    type: "reminder",
    createdAt: now,
    updatedAt: now,
  },
];

export const demoQuizzes: QuizDocument[] = [
  {
    schemaVersion: 1,
    id: "q1",
    workspaceId: demoWorkspace.id,
    classId: "c1",
    title: "資訊素養小測",
    sourceName: "課堂筆記",
    difficulty: "standard",
    questions: [
      {
        id: "q1",
        question: "遇到來源不明的網上資訊，第一步應該怎樣做？",
        options: ["立即轉發", "查核消息來源", "只看留言數量", "相信標題"],
        correctIndex: 1,
        explanation: "先查核來源能協助判斷資訊是否可靠。",
      },
      {
        id: "q2",
        question: "哪一項最能減少個人資料外洩風險？",
        options: ["重用同一密碼", "公開出生日期", "啟用雙重驗證", "接受所有權限"],
        correctIndex: 2,
        explanation: "雙重驗證能在密碼外增加一道保障。",
      },
    ],
    createdAt: now,
    updatedAt: now,
  },
];

// Only add past-term examples once a term in the current school year has ended.
const previousTerm = [...demoWorkspace.academicTerms!].reverse().find((term) => term.endDate < demoDate(0));
if (previousTerm) {
  const previousDate = (index: number) =>
    new Date(new Date(`${previousTerm.endDate}T12:00:00Z`).getTime() - (20 + index) * 86400_000)
      .toISOString()
      .slice(0, 10);
  const oldAssignments = demoHomeworkAssignments.map((item, index) => ({
    ...item,
    id: `previous-${item.id}`,
    dueDate: demoSchoolDate(previousDate(index), demoSchoolCalendar),
    note: "虛構上學期示例",
  }));
  const oldHomeworkRecords = demoHomeworkRecords.map((item) => ({
    ...item,
    id: `previous-${item.id}`,
    assignmentId: `previous-${item.assignmentId}`,
    students: Object.fromEntries(
      Object.entries(item.students).map(([studentId, entry]) => [
        studentId,
        { ...entry, score: entry.score === undefined ? undefined : Math.round(entry.score * 0.75) },
      ]),
    ),
  }));
  demoHomeworkAssignments.push(...oldAssignments);
  demoHomeworkRecords.push(...oldHomeworkRecords);
  demoDailyRecords.push(
    ...demoDailyRecords.map((item, index) => ({
      ...item,
      id: `previous-${item.id}`,
      date: previousDate(index % 6),
      students: Object.fromEntries(
        Object.entries(item.students).map(([studentId, entry]) => [
          studentId,
          { ...entry, answers: Math.max(0, entry.answers - 1) },
        ]),
      ),
    })),
  );
  demoAttendanceRecords.push(
    ...demoAttendanceRecords.map((item, index) => ({
      ...item,
      id: `previous-${item.id}`,
      date: previousDate(index),
      exceptions: { ...item.exceptions, [item.studentIds[0]!]: "absent" as const },
    })),
  );
  demoHomeroomHomeworkRecords.push(
    ...demoHomeroomHomeworkRecords.map((item, index) => ({
      ...item,
      id: `previous-${item.id}`,
      date: previousDate(index),
      missingStudentIds: [
        ...new Set([
          ...item.missingStudentIds,
          ...demoStudents
            .filter((student) => student.classId === item.classId)
            .slice(0, 3)
            .map((student) => student.id),
        ]),
      ],
    })),
  );
}
